<script setup lang="ts">
import { useScroll } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

import type {
  HotelFilter,
  HotelFilterUpdate,
} from '@apps/main/new-desktop/modules/hotelFilters';
import { DEFAULT_HOTEL_FILTER_UPDATE } from '@apps/main/new-desktop/modules/hotelFilters';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import type { AppMaskType } from '@libs/ui';
import { AppMask, AppTag, arrivedStateToMaskType } from '@libs/ui';

import { useHotelFiltersStore } from './store/hotelFiltersStore';

interface HotelSelectionFiltersTagsProps {
  ingredient?: HotelIngredient | OwnStayIngredient | null;
}

const props = defineProps<HotelSelectionFiltersTagsProps>();

const hotelFiltersStore = useHotelFiltersStore();

interface HotelFiltersTagsEmits {
  (eventName: 'update-filter', updateEvent: HotelFilterUpdate): void;
}

const emit = defineEmits<HotelFiltersTagsEmits>();

watch(
  () => props.ingredient,
  () => {
    if (props.ingredient) {
      hotelFiltersStore.setFiltersValuesFromIngredient(props.ingredient);
    }
  },
  { immediate: true, deep: true }
);

const pillsContainerRef = ref();

const { arrivedState } = useScroll(pillsContainerRef, {
  behavior: 'smooth',
});

const maskType = computed<AppMaskType>(() =>
  arrivedStateToMaskType(arrivedState)
);

function onUpdateFilter(type: HotelFilter): void {
  emit('update-filter', DEFAULT_HOTEL_FILTER_UPDATE[type]);
}
</script>

<template>
  <AppMask :type="maskType">
    <div ref="pillsContainerRef" class="hotel-selection-filters-pills">
      <AppTag
        v-for="pill of hotelFiltersStore.activeFilters"
        :key="pill.type"
        class="tag"
        type="filter"
        :icon="{
          name: 'closeInCircle',
          size: '18',
          position: 'right',
          color: 'color-neutral-70',
          hoverColor: 'color-neutral-70',
          clickable: true,
          ariaLabel: $t('a11y.remove_filter'),
        }"
        :text-wrap="false"
        @icon-click="onUpdateFilter(pill.type)"
      >
        <template #title>{{ pill.text }}</template>
      </AppTag>
    </div>
  </AppMask>
</template>

<style scoped lang="scss">
.hotel-selection-filters-pills {
  --section-height: 40px;

  display: flex;
  gap: var(--spacing-8);
  overflow-x: hidden;
  height: var(--section-height);
  padding-bottom: var(--spacing-8);

  @include on-hover {
    overflow-x: auto;
  }

  overflow-y: hidden;

  @include for-touch-devices {
    overflow-x: auto;
  }

  .tag {
    height: var(--spacing-32);
  }
}
</style>
